import { IconName, IconPrefix } from "@fortawesome/fontawesome-svg-core";
import cn from "classnames";
import React, { ReactNode, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { NavItem, NavLink, UncontrolledTooltip } from "reactstrap";
import { useBreakpointBelowLg } from "../../../hooks/useBreakpointBelowLg";
import { useGlobalCore } from "../../../cores/globals";
import { Icon } from "../companies/icons/Icon";
import { showSidebarDisabledToast } from "./sidebarDisabledMessages";
import { sidebarNavDomId } from "./sidebarDomId";

interface Props {
  onClick?: () => void;
  iconName?: IconName;
  iconPrefix?: IconPrefix;
  icon?: ReactNode;
  label: string;
  disabled?: boolean;
  disabledMessage?: string;
  activeUrl?: string;
}

export const NavItem2 = (props: Props) => {
  const {
    onClick,
    iconName,
    iconPrefix,
    label,
    icon,
    disabled,
    activeUrl,
    disabledMessage,
  } = props;
  const { pathname } = useLocation();
  const { collapseSideBar, setOpenSideBar } = useGlobalCore();
  const isMobileSidebar = useBreakpointBelowLg();
  const isCollapsedDesktop = collapseSideBar && !isMobileSidebar;
  const showItemLabel = !collapseSideBar || isMobileSidebar;
  const itemDomId = useMemo(
    () => sidebarNavDomId(activeUrl, label),
    [activeUrl, label]
  );

  const handleClick = useCallback(() => {
    if (disabled) {
      showSidebarDisabledToast(
        disabledMessage,
        `sidebar-disabled-${itemDomId}`
      );
      return;
    }
    if (isMobileSidebar) setOpenSideBar(false);
    onClick?.();
  }, [disabled, disabledMessage, isMobileSidebar, itemDomId, onClick, setOpenSideBar]);

  return (
    <>
      <NavItem
        id={itemDomId}
        className={cn("mb-2 sidebar-item", {
          pointer: !disabled,
          "active-item": pathname === activeUrl && !disabled,
        })}
      >
        <NavLink
          onClick={handleClick}
          disabled={disabled}
          className={cn({
            "sidebar-link-collapsed": isCollapsedDesktop,
          })}
        >
          <span className="sidebar-icon-slot">
            {!icon && iconName && (
              <Icon
                className="item-icon"
                name={iconName}
                prefix={iconPrefix}
              />
            )}
            {icon != null && icon}
          </span>
          {showItemLabel && <span className="ml-3 item-label">{label}</span>}
        </NavLink>
      </NavItem>
      {disabledMessage && disabled && (
        <UncontrolledTooltip placement="right" target={itemDomId}>
          {disabledMessage}
        </UncontrolledTooltip>
      )}
    </>
  );
};
